import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Stack, Text, useViewport } from "@bookingcom/bui-react";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { trackWithDefaultStage } from "@flights/et-universal";

import Container from "../Container";
import { GeniusAirlinesBanner, GeniusAirlinesBannerMobile } from "../GeniusAirlinesBanner";
import SouthwestAirlinesBanner from "../SouthwestAirlinesBanner";
import { mcn } from "../../../utils/mergeClassnames";

import styles from "./HomepageOffers.module.css";
import { getIndexCampaigns } from "../../../store/indexCampaigns/selectors";
import useHomeRecentSearch from "../HomeRecentSearch/useHomeRecentSearch.controller";
import useFlightsWebSouthwest from "../../../utils/experiments/funnel/flights_web_southwest";
import useGeniusAirlinesBanner from "../../../utils/experiments/genius/web_flights_lve_index_genius_airlines_banner";

const HomepageOffers = ({ isMobile }: { isMobile: boolean }) => {
  const { isLarge } = useViewport();
  const geniusAirlineData = useSelector(getIndexCampaigns).geniusAirlines;
  const i18n = useI18n();
  const { searchHistoryList } = useHomeRecentSearch();
  const geniusAirlinesBanner = useGeniusAirlinesBanner();

  const geniusStyles = mcn(isLarge ? styles.item : styles.itemNoWidth, styles.itemSingle);

  const flightsWebSouthwest = useFlightsWebSouthwest();

  const showFirstBanner = useMemo(() => {
    return flightsWebSouthwest.isVariant;
  }, [flightsWebSouthwest]);

  // Start - web_flights_lve_index_genius_airlines_banner
  const showGeniusBanner = geniusAirlineData?.airlines && geniusAirlinesBanner.trackStagesAndGetVariant();
  if (geniusAirlineData?.airlines && searchHistoryList.length !== 0 && !isMobile)
    geniusAirlinesBanner.trackHasRecentSearch();
  // End - web_flights_lve_index_genius_airlines_banner

  useEffect(() => {
    flightsWebSouthwest.trackStages();
  }, [flightsWebSouthwest]);

  if (!showFirstBanner && !showGeniusBanner) return null;

  const hasSmallMargin =
    !trackWithDefaultStage("flights_web_cat_responsive_fixes_www", 1) &&
    (!searchHistoryList || searchHistoryList.length === 0);

  return (
    <Container
      className={isMobile ? styles.mobileContainer : styles.container}
      style={{ marginBlockStart: hasSmallMargin ? 0 : undefined }}
    >
      {showGeniusBanner ? (
        <Stack direction="column" gap={0} className={styles.top}>
          <Text variant="headline_2" tagName={"h2"}>
            {i18n.trans(t("flights_index_offers_carousel_header"))}
          </Text>
          <Text variant="body_1" color="neutral_alt">
            {i18n.trans(t("flights_index_offers_carousel_subheader"))}
          </Text>
        </Stack>
      ) : null}
      <Stack direction={!isLarge ? "column" : "row"} alignItems="stretch" justifyContent="center" gap={4} wrap="wrap">
        {showFirstBanner ? (
          <Stack.Item className={isLarge && geniusAirlineData ? styles.item : styles.itemNoWidth}>
            <SouthwestAirlinesBanner />
          </Stack.Item>
        ) : null}
        {showGeniusBanner ? (
          <Stack.Item className={geniusStyles}>
            {isMobile ? (
              <GeniusAirlinesBannerMobile geniusAirlineBannerData={geniusAirlineData} />
            ) : (
              <GeniusAirlinesBanner geniusAirlineBannerData={geniusAirlineData} isSecondaryOffer={showFirstBanner} />
            )}
          </Stack.Item>
        ) : null}
      </Stack>
    </Container>
  );
};

export default HomepageOffers;
